import { createAsyncThunk } from '@reduxjs/toolkit'

import { FeatureKey } from '@/constants/feature-key'
import {
  MAX_PAGE_SIZE,
  Pagination,
} from '@/constants/pagination'
import { Employee } from '@/model/employee'
import { createAppAsyncThunk } from '@/redux/utils'
import { axios } from '@/utils/axios'
import {
  getFormatedComputerTimezone,
  timezoneFormat,
} from '@/utils/timezone'

export const employeeNamespace = 'employee'

export interface ReadAllEmployeeParams {
  permissionKey?: FeatureKey
}

export const readAllEmployees = createAppAsyncThunk(
  `${employeeNamespace}/readAllEmployees`,
  async ({ permissionKey }: ReadAllEmployeeParams | undefined = {}, { getState }) => {
    const { global } = getState()

    const finalPermissionKey = permissionKey || global.permissionKey
    const { data } = await axios.get<Pagination<Employee>>('/employee/all', {
      params: {
        pageSize: MAX_PAGE_SIZE,
        permissionKey: finalPermissionKey,
      },
    })
    return { data, permissionKey: finalPermissionKey }
  },
)

export type UpdateMineEmployeeParams = Pick<
  Employee,
  | 'reportTimezone'
  | 'regularTimezone'
  | 'weeklyReportOn'
  | 'showSystemNotification'
  | 'showOFMessageNotification'
  | 'enableOFMessageNotificationSound'
>

export const updateMineEmployee = createAsyncThunk(
  `${employeeNamespace}/updateMineEmployee`,
  async (data: UpdateMineEmployeeParams) =>
    (await axios.put<Employee>('/employee/mine', data)).data,
)
export const readMineEmployee = createAsyncThunk(
  `${employeeNamespace}/readMineEmployee`,
  async () => (await axios.get<Employee>('/employee/mine')).data,
)
export type CreateEmployeeParams = Omit<Employee, 'id' | 'status'>;
export const createEmployee = createAppAsyncThunk(
  `${employeeNamespace}/createEmployee`,
  async (params: CreateEmployeeParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const { data } = await axios.post<Employee>('/employee', {
      ...params,
      reportTimezone: timezoneFormat(0),
      regularTimezone: getFormatedComputerTimezone(),
    })
    return { data, permissionKey }
  },
)
export type UpdateEmployeeParams = Partial<
  Omit<Employee, 'organizationId' | 'status'>
>
export const updateEmployee = createAsyncThunk(
  `${employeeNamespace}/updateEmployee`,
  async (data: UpdateEmployeeParams) =>
    (await axios.put<Employee>(`/employee/${data.id}`, data)).data,
)
export const removeEmployee = createAsyncThunk(
  `${employeeNamespace}/removeEmployee`,
  async (id: number) => await axios.delete(`/employee/${id}`),
)
export const activateEmployee = createAsyncThunk(
  `${employeeNamespace}/activateEmployee`,
  async (id: number) =>
    (await axios.post<Employee>(`/employee/${id}/action/activate`)).data,
)
export const deactivateEmployee = createAsyncThunk(
  `${employeeNamespace}/deactivateEmployee`,
  async (id: number) =>
    (await axios.post<Employee>(`/employee/${id}/action/deactivate`)).data,
)
